"use client";

import GradientAvatar from "./GradientAvatar";

interface Props {
  /** Optional uploaded profile image URL. When present this is rendered as-is. */
  src?: string | null;
  /**
   * Deterministic seed for the autogenerated fallback. Prefer the leader's
   * wallet address (same wallet = same gradient forever); falls back to the
   * username if address isn't known at the call site.
   */
  address?: string | null;
  /** Square side length in px. */
  size?: number;
  /** Optional extra classes for the wrapper. */
  className?: string;
  /** Alt text for the rendered <img> tag. */
  alt?: string;
}

/**
 * Canonical leader avatar used across portfolio / copytrading / notifications.
 * Uploaded profile image when available, otherwise a deterministic GradientAvatar
 * seeded by wallet address (same algorithm as the trader OG preview card, so
 * the autogenerated avatar matches what the user sees on /traders/<addr>).
 * Always round — `unify everywhere` is the whole point of this component.
 */
export default function LeaderAvatar({
  src,
  address,
  size = 12,
  className = "",
  alt = "",
}: Props) {
  const dim = { width: size, height: size };

  if (src) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={src}
        alt={alt}
        className={`rounded-full object-cover flex-shrink-0 ${className}`}
        style={dim}
        loading="lazy"
      />
    );
  }

  if (address) {
    return (
      <GradientAvatar
        seed={address}
        size={size}
        className={`!rounded-full ${className}`}
      />
    );
  }

  // No src and no seed — flat circle so nothing renders as a "wrong leader".
  return (
    <span
      className={`inline-block rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0 ${className}`}
      style={dim}
      aria-hidden="true"
    />
  );
}
